import { IResultResponse, IUseFetchOptionsParams } from '@common/api.models';
import { isValidApiBaseProps } from '@common/api.services';
import { FETCH_QUERIES_NAME, MUTATION_KEYS } from '@common/constants';
import {
  createUseFetchOptionsCustomPropsFn,
  createUseFetchOptionsCustomPropsWithSearchFn,
} from '@common/selectOptions.services';
import { AxiosErrorResponse } from '@core/error.models';
import { isPositiveNumber } from '@core/Primitives/number.services';
import { IItemResponse } from '@gcl/react-core';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  ICreateProjectTeamRequest,
  IProjectTeam,
  IProjectTeamOption,
  IUpdateProjectTeamRequest,
} from './projectTeam.models';

import {
  createProjectTeamAsync,
  deleteProjectTeamAsync,
  getProjectTeamsWithUserAsync,
  getTeamDistributionOptionsWithSearchAsync,
  getTeamOptionsByCompanyCodeAsync,
  getTeamOptionsByCompanyIdAsync,
  getTeamOptionsByCompanyTypeIdOrganizationTypeIdAsync,
  IDeleteProjectTeamProps,
  IGetProjectTeamsWithUserProps,
  IGetTeamDistributionOptionsWithSearchProps,
  updateProjectTeamAsync,
} from './projectTeam.services';

export const useGetProjectTeamsWithUserAsync = (props: IGetProjectTeamsWithUserProps, enabled = true) => {
  return useQuery<IProjectTeam[], AxiosErrorResponse>(
    [props.projectId, props.companyId, FETCH_QUERIES_NAME.TEAMS_WITH_USER],
    async () => {
      const result = await getProjectTeamsWithUserAsync(props);

      return result.items || [];
    },
    {
      enabled: isValidApiBaseProps(props) && isPositiveNumber(props.companyId) && enabled,
    },
  );
};

export const useCreateProjectTeamAsync = () => {
  return useMutation<IItemResponse<IProjectTeam>, AxiosErrorResponse, ICreateProjectTeamRequest, unknown>(
    [MUTATION_KEYS.CREATE_PROJECT_TEAM],
    async (createRequest: ICreateProjectTeamRequest) => {
      const result = await createProjectTeamAsync(createRequest);
      return result;
    },
  );
};

export const useUpdateProjectTeamAsync = () => {
  return useMutation<IItemResponse<IProjectTeam>, AxiosErrorResponse, IUpdateProjectTeamRequest, unknown>(
    [MUTATION_KEYS.UPDATE_PROJECT_TEAM],
    async (updateRequest: IUpdateProjectTeamRequest) => {
      const result = await updateProjectTeamAsync(updateRequest);
      return result;
    },
  );
};

export const useDeleteProjectTeamAsync = () => {
  return useMutation<IResultResponse, AxiosErrorResponse, IDeleteProjectTeamProps, unknown>(
    [MUTATION_KEYS.DELETE_PROJECT_TEAM],
    async (deleteProps: IDeleteProjectTeamProps) => {
      const result = await deleteProjectTeamAsync(deleteProps);
      return result;
    },
  );
};

export const useFetchTeamsOptionsByCompanyTypeIdOrganizationTypeId = ({
  isDirty = false,
  shouldConvertOptions = true,
}: IUseFetchOptionsParams) =>
  createUseFetchOptionsCustomPropsFn({
    queryName: FETCH_QUERIES_NAME.TEAMS_OPTIONS_BY_COMPANY_TYPE_ID_ORGANIZATION_TYPE_ID,
    getOptionsFn: getTeamOptionsByCompanyTypeIdOrganizationTypeIdAsync,
    handleOptionsProps: { shouldConvertOptions, textFieldName: 'teamName' },
    enabled: !isDirty,
  });
export const useFetchTeamsOptionsByCompanyCode = ({
  isDirty = false,
  shouldConvertOptions = true,
}: IUseFetchOptionsParams) =>
  createUseFetchOptionsCustomPropsFn({
    queryName: FETCH_QUERIES_NAME.TEAMS_OPTIONS_BY_COMPANY_CODE,
    getOptionsFn: getTeamOptionsByCompanyCodeAsync,
    handleOptionsProps: { shouldConvertOptions, textFieldName: 'teamName' },
    enabled: !isDirty,
  });

export const useFetchTeamsOptionsByCompanyId = ({
  isDirty = false,
  shouldConvertOptions = true,
}: IUseFetchOptionsParams) =>
  createUseFetchOptionsCustomPropsFn({
    queryName: FETCH_QUERIES_NAME.TEAMS_OPTIONS_BY_COMPANY_ID,
    getOptionsFn: getTeamOptionsByCompanyIdAsync,
    handleOptionsProps: { shouldConvertOptions, textFieldName: 'teamName' },
    enabled: !isDirty,
  });

export const useFetchTeamDistributionOptionsWithSearch = ({
  isDirty = false,
  shouldConvertOptions = false,
}: IUseFetchOptionsParams) =>
  createUseFetchOptionsCustomPropsWithSearchFn<IProjectTeamOption, IGetTeamDistributionOptionsWithSearchProps>({
    queryName: FETCH_QUERIES_NAME.TEAM_DISTRIBUTION_OPTIONS_SEARCH,
    getOptionsWithSearchFn: getTeamDistributionOptionsWithSearchAsync,
    handleOptionsProps: { shouldConvertOptions },
    enabled: !isDirty,
  });
